import { useCallback, useMemo, useState } from 'react';
import { BottomSection, HeaderRightSection, HeaderSection, HeaderTitle, MiddleSection, Pricing } from './styles';
import Tabs from '../../tabs';
import {
  TAB_BUTTON_TYPE,
  PLAN_UPGRADE_TYPE,
  BUTTON_TYPE,
  USERS_PLAN_TYPE,
  USER_ROLE,
  SUBSCRIPTION_PLAN
} from '../../../global/constants';
import { planUpgradeModelData, planUpgradeModelOption } from '../../../global/row';
import Button from '../../button';
import PlanCard from './planCard';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { getSubscriptionUrl } from '../../../services/settingServices';
import { getCurrentDomain } from '../../../helpers/common';
import { captureException } from '../../../services/logService';

const PlanUpgradeModalStep1 = () => {
  // Mobx store variables
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  // state variables
  const [selectedPlanType, setSelectedPlanType] = useState(PLAN_UPGRADE_TYPE.YEARLY);
  const [usersPlanSelected, setUsersPlanSelected] = useState(USERS_PLAN_TYPE.USERS);
  const [loading, setLoading] = useState(false);

  const getUsersCount = useMemo(() => {
    const usersCount = currentWorkspace?.users?.filter((user) => user.role !== USER_ROLE.CLIENT).length;
    return usersCount || 1;
  }, [currentWorkspace?.users]);

  const getTotalAmount = useMemo(() => {
    let totalPrice;
    if (selectedPlanType === PLAN_UPGRADE_TYPE.YEARLY && usersPlanSelected === USERS_PLAN_TYPE.USERS)
      totalPrice = planUpgradeModelData[PLAN_UPGRADE_TYPE.YEARLY][0].price * getUsersCount * 12;
    else if (selectedPlanType === PLAN_UPGRADE_TYPE.YEARLY && usersPlanSelected === USERS_PLAN_TYPE.UNLIMITED)
      totalPrice = planUpgradeModelData[PLAN_UPGRADE_TYPE.YEARLY][1].price * 12;
    else if (selectedPlanType === PLAN_UPGRADE_TYPE.MONTHLY && usersPlanSelected === USERS_PLAN_TYPE.USERS)
      totalPrice = planUpgradeModelData[PLAN_UPGRADE_TYPE.MONTHLY][0].price * getUsersCount;
    else if (selectedPlanType === PLAN_UPGRADE_TYPE.MONTHLY && usersPlanSelected === USERS_PLAN_TYPE.UNLIMITED)
      totalPrice = planUpgradeModelData[PLAN_UPGRADE_TYPE.MONTHLY][1].price;
    return totalPrice;
  }, [getUsersCount, selectedPlanType, usersPlanSelected]);

  const handleTabChange = useCallback((tabKey: number) => {
    setSelectedPlanType(tabKey);
  }, []);

  // Memoized renderer for the Tabs to avoid remounting on unrelated re-renders
  const renderTabOption = useCallback(() => {
    return planUpgradeModelOption?.map((item) => {
      return (
        <Tabs
          key={item?.key}
          variant={TAB_BUTTON_TYPE.STANDARD}
          title={item?.label}
          size='small'
          onClick={() => handleTabChange(item?.key)}
          isActive={selectedPlanType === item?.key}
          className='tabs'
        />
      );
    });
  }, [handleTabChange, selectedPlanType]);

  const handleCheckboxClick = useCallback((index: number) => {
    setUsersPlanSelected(index);
  }, []);

  const handleUpgradeNow = useCallback(async () => {
    try {
      setLoading(true);
      let selectedPlan;
      if (selectedPlanType === PLAN_UPGRADE_TYPE.YEARLY && usersPlanSelected === USERS_PLAN_TYPE.USERS)
        selectedPlan = SUBSCRIPTION_PLAN.YEARLY_PAYMENT_SPECIFIC_USERS;
      else if (selectedPlanType === PLAN_UPGRADE_TYPE.YEARLY && usersPlanSelected === USERS_PLAN_TYPE.UNLIMITED)
        selectedPlan = SUBSCRIPTION_PLAN.YEARLY_PAYMENT_UNLIMITED_USERS;
      else if (selectedPlanType === PLAN_UPGRADE_TYPE.MONTHLY && usersPlanSelected === USERS_PLAN_TYPE.USERS)
        selectedPlan = SUBSCRIPTION_PLAN.MONTHLY_PAYMENT_SPECIFIC_USERS;
      else if (selectedPlanType === PLAN_UPGRADE_TYPE.MONTHLY && usersPlanSelected === USERS_PLAN_TYPE.UNLIMITED)
        selectedPlan = SUBSCRIPTION_PLAN.MONTHLY_PAYMENT_UNLIMITED_USERS;

      const payload = {
        successUrl: `https://${getCurrentDomain()}/settings/subscription?status=success`,
        selectedPlan,
        workspaceUsers: usersPlanSelected === USERS_PLAN_TYPE.USERS ? getUsersCount : undefined
      };

      const response = await getSubscriptionUrl(payload);
      if (response?.subscriptionUrl) {
        window.open(response?.subscriptionUrl, '_blank');
      } else {
        console.error('URL not found in response');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [getUsersCount, selectedPlanType, usersPlanSelected]);

  return (
    <>
      <HeaderSection>
        {/* Title and billing cadence tabs */}
        <HeaderTitle>Upgrade your plan</HeaderTitle>
        <HeaderRightSection>{renderTabOption()}</HeaderRightSection>
      </HeaderSection>
      <MiddleSection>
        {/* Plan cards */}
        {planUpgradeModelData[selectedPlanType]?.map((item, index) => (
          <PlanCard
            key={item.title}
            features={item.features}
            label={item.title}
            price={`$${item.price}`}
            usersCount={index === 0 ? getUsersCount : 0}
            priceTag={item.priceTag}
            isSelected={usersPlanSelected === index + 1}
            onCheckboxClick={() => handleCheckboxClick(index + 1)}
          />
        ))}
        {/* <PlanCard features={unlimitedSeatFeature} label='Unlimited Seats' price='$99' 
        priceTag='unlimited users' /> */}
      </MiddleSection>
      <BottomSection>
        {/* Footer pricing summary and CTA */}
        <Pricing>
          ${getTotalAmount} / {selectedPlanType === PLAN_UPGRADE_TYPE.YEARLY ? 'Year' : 'Month'}
        </Pricing>
        <Button
          title='Pay Now'
          variant={BUTTON_TYPE.PRIMARY}
          size='small'
          buttonType='button'
          onClick={handleUpgradeNow}
          isLoading={loading}
        />
      </BottomSection>
    </>
  );
};

export default observer(PlanUpgradeModalStep1);
